/**
 * Admin API Client
 *
 * Axios-based client for admin dashboard API operations.
 *
 * Features:
 * - HTTP-only session cookies (primary auth)
 * - API key header (fallback auth)
 * - Fast retry for failed reads (GET/HEAD only; never writes, cancels or timeouts)
 * - Rate limit handling with user feedback
 * - Request cancellation via AbortController
 */

import { announceTenantSwitch, isTenantMovedCode, reloadIntoCurrentTenant, tenantPinHeaders } from './tenantPin';
import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import { getRouterRef } from '../lib/routerRef';
import { loginHref, redirectToCurrentPage } from '../auth/safeRedirect';
import type { ClusterStatus } from '../types/cluster';
import {
  API_RETRY_CONFIG,
  API_TIMEOUT_CONFIG,
  HTTP_STATUS,
  calculateRetryDelay,
  isRetryableStatusCode,
} from '../config/api';

// Event emitter for rate limit notifications using modern EventTarget pattern
export interface RateLimitEventDetail {
  retryAfter: number;
}

class RateLimitEmitter extends EventTarget {
  emit(retryAfter: number): void {
    this.dispatchEvent(
      new CustomEvent<RateLimitEventDetail>('ratelimit', {
        detail: { retryAfter },
      })
    );
  }
}

export const rateLimitEmitter = new RateLimitEmitter();

/**
 * Subscribe to rate limit events
 * @deprecated Use rateLimitEmitter.addEventListener instead
 */
export function onRateLimit(callback: (retryAfter: number) => void): () => void {
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<RateLimitEventDetail>;
    callback(customEvent.detail.retryAfter);
  };
  rateLimitEmitter.addEventListener('ratelimit', handler);
  return () => rateLimitEmitter.removeEventListener('ratelimit', handler);
}

/** GET and HEAD are safe to send again; a config with no method is a GET. */
function isIdempotentRead(config: { method?: string }): boolean {
  const method = (config.method ?? 'get').toLowerCase();
  return method === 'get' || method === 'head';
}

/**
 * True for a request the user (or React Query) aborted. The same check as
 * `axios.isCancel`, kept local so the interceptor reads only the error.
 */
function isCancelled(error: AxiosError): boolean {
  return (error as { __CANCEL__?: boolean }).__CANCEL__ === true || error.code === 'ERR_CANCELED';
}

/** Retry-After in seconds, or undefined when the header is absent or unparsable. */
function retryAfterSeconds(error: AxiosError): number | undefined {
  const raw = error.response?.headers?.['retry-after'];
  const seconds = parseInt(String(raw ?? ''), 10);
  return Number.isFinite(seconds) ? seconds : undefined;
}

/**
 * Whether the generic retry block may send this request again. Writes are
 * never re-sent (a 503 can arrive after the write landed, and a second POST
 * duplicates it); cancellations and timeouts are final; a 429 is retried
 * only when the server asks for a short wait.
 */
function isRetryableError(error: AxiosError, config: { method?: string }): boolean {
  if (!isIdempotentRead(config)) return false;
  if (isCancelled(error)) return false;
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return false;
  if (!error.response) return true; // Network error
  if (error.response.status === HTTP_STATUS.TOO_MANY_REQUESTS) {
    const wait = retryAfterSeconds(error);
    return wait !== undefined && wait <= API_RETRY_CONFIG.maxRetryAfterSeconds;
  }
  return isRetryableStatusCode(error.response.status);
}

import type {
  AdminUser,
  AdminRole,
  User,
  UserProfile,
  CreateUserInput,
  CreateUserResult,
  PasswordResetIssued,
  UpdateUserInput,
  UserFilter,
  PaginatedUsersResponse,
  ApiKeysResponse,
  ApiKeyListing,
  CreateApiKeyInput,
  IssuedApiKey,
  RotatedApiKey,
  RotationOverlapHours,
  SystemMetrics,
  BusinessMetrics,
  TimeSeries,
  Report,
  ReportFilter,
  AuditLogEntry,
  AuditLogDetail,
  AuditLogFilter,
  AuditLogStats,
  Agent,
  CreateAgentInput,
  UpdateAgentInput,
  ABTest,
  EvaluationFeedback,
  ConversationQuality,
  FailedConversation,
  EvaluationStats,
  AudioQualityMetrics,
  ActiveCall,
  AudioSystemStats,
  GuardrailEvent,
  GuardrailStats,
  EmailTemplate,
  EmailTemplateVersion,
  CreateEmailTemplateInput,
  UpdateEmailTemplateInput,
  EmailTemplatePreview,
  VoiceConfig,
  CreateVoiceConfigInput,
  UpdateVoiceConfigInput,
  TTSProvider,
  TTSProviderType,
  ToolCredentialsResponse,
  SetToolCredentialsRequest,
  TestToolConnectionRequest,
  TestToolConnectionResponse,
  IntegrationProfileSummary,
  IntegrationProfileDetail,
  TenantPhoneNumber,
  UpdatePhoneNumberDetailsInput,
  PhoneNumberHistoryPage,
  NumberPurchaseOptions,
  AvailablePhoneNumber,
  NumberTerms,
  CallerIdVerificationStarted,
  GivenBackPhoneNumber,
  CreateIntegrationProfileInput,
  UpdateIntegrationProfileInput,
  TestIntegrationConnectionRequest,
  TestIntegrationConnectionResponse,
  Conversation,
  ConversationMessage,
  ConversationDiagnostics,
  ConversationRecording,
  ConversationFilter,
  ConversationStats,
  ConversationsOverview,
  ConversationsOverviewQuery,
  PaginatedConversations,
  CatalogOption,
  VoiceOption,
  LlmProviderAvailability,
  AgentVersionPage,
  AgentVersionComparison,
  AgentVersionCompareTarget,
  AgentVersionRestoreResult,
} from '../types/admin';

/**
 * Shape a password-reset route's body for callers. `kind` and `ttl_minutes`
 * are copied only when the server sent them (it omits both on the SwiftGate
 * path), so a caller can tell "SwiftGate emailed it" from "Switchboard emailed
 * a link that expires in N minutes" without inventing either.
 */
function pickPasswordResetIssued(data: {
  message: string;
  kind?: PasswordResetIssued['kind'];
  ttl_minutes?: number;
}): PasswordResetIssued {
  return {
    message: data.message,
    ...(data.kind !== undefined ? { kind: data.kind } : {}),
    ...(data.ttl_minutes !== undefined ? { ttl_minutes: data.ttl_minutes } : {}),
  };
}

// Extended config type with retry metadata
interface RetryConfig extends InternalAxiosRequestConfig {
  __retryCount?: number;
  __skipRetry?: boolean;
  __sessionRefreshRetried?: boolean;
}

/**
 * Emitted when the server has confirmed the session is genuinely unrecoverable
 * (after auto-retry has been exhausted). Components can subscribe to surface a
 * toast before the redirect to /admin/login lands.
 */
class SessionExpiredEmitter extends EventTarget {
  // Dedupe: a burst of in-flight requests can each 401 at once. Emit the
  // "expired" toast only once per expiry episode so the user doesn't get a
  // stack of identical toasts. `reset()` re-arms it once a request succeeds
  // (i.e. the session recovered / the user signed back in).
  private active = false;
  emit(): void {
    if (this.active) return;
    this.active = true;
    this.dispatchEvent(new Event('expired'));
  }
  reset(): void {
    this.active = false;
  }
}
export const sessionExpiredEmitter = new SessionExpiredEmitter();

class AdminApiClient {
  private client: AxiosInstance;
  private abortControllers: Map<string, AbortController> = new Map();

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL ?? '/api',
      timeout: API_TIMEOUT_CONFIG.defaultMs,
      headers: {
        'Content-Type': 'application/json',
      },
      // Enable cookies for cross-origin requests (needed for HTTP-only cookies)
      withCredentials: true,
    });

    // Request interceptor: pin writes to the tenant the page was loaded for,
    // and let reads fail faster than writes. A timeout a caller set per
    // request differs from the default and is left alone.
    this.client.interceptors.request.use(
      (config) => {
        Object.assign(config.headers, tenantPinHeaders(config.method));
        if (isIdempotentRead(config) && config.timeout === API_TIMEOUT_CONFIG.defaultMs) {
          config.timeout = API_TIMEOUT_CONFIG.readMs;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling with retry logic
    this.client.interceptors.response.use(
      (response) => {
        // A successful response means auth is healthy — re-arm the expiry toast
        // so a future genuine expiry surfaces exactly one notification.
        sessionExpiredEmitter.reset();
        return response;
      },
      async (error: AxiosError) => {
        const config = error.config as RetryConfig | undefined;

        // The session moved to another tenant (another tab switched, or the
        // switch ran out of time): nothing on this page is current any more.
        // An expiry was announced by nobody, so tell the other tabs too.
        const movedCode = (error.response?.data as { code?: string } | undefined)?.code;
        if (isTenantMovedCode(movedCode)) {
          if (movedCode === 'SWITCH_EXPIRED') announceTenantSwitch();
          reloadIntoCurrentTenant();
          return Promise.reject(error);
        }

        // Handle 401 Unauthorized
        if (error.response?.status === 401) {
          const data = error.response.data as { code?: string } | undefined;

          // SESSION_REFRESH_REQUIRED is usually a transient race in the
          // backend's token-refresh path (a parallel request rotated the
          // refresh token first). Retry the original request once after a
          // short delay so the persisted rotated tokens land. If it fails
          // again, fall through to the genuine-expiry path below.
          if (
            data?.code === 'SESSION_REFRESH_REQUIRED' &&
            config &&
            !config.__sessionRefreshRetried
          ) {
            config.__sessionRefreshRetried = true;
            await new Promise((resolve) => { setTimeout(resolve, 250); });
            return this.client.request(config);
          }

          sessionExpiredEmitter.emit();
          if (!window.location.pathname.includes('/admin/login')) {
            // Signing back in returns to this page.
            const back = redirectToCurrentPage();
            const router = getRouterRef();
            if (router) {
              router.navigate({ to: '/admin/login', search: back ? { redirect: back } : {} });
            } else {
              window.location.href = loginHref(back);
            }
          }
          return Promise.reject(error);
        }

        // Handle 403 Forbidden — route to the friendly Unauthorized page,
        // skipping endpoints whose 403s are caller-handled (session/profile
        // probes during auth resolution can legitimately 403).
        //
        // Navigate with `forbidden: '1'` (not a fabricated `required` role).
        // The route guard `requireMinimumRole(min)` is the only producer of
        // an honest `required` claim; the interceptor doesn't know what the
        // server required, so claiming "admin" here misled incident-triage
        // when the real requirement was the platform admin grant. The actual required
        // permission, when the server knows it, is now in `error.response.
        // data.required` for logs / future ops UI to consume.
        if (error.response?.status === 403) {
          const url = config?.url ?? '';
          const isAuthProbe = url.includes('/admin/auth/profile')
            || url.includes('/admin/auth/session');
          // The key routes refuse with API_NOT_ENABLED when Livepoint has
          // switched the API off; that is not a missing permission, and the
          // Developer API page shows the message where the user acted.
          const isApiSwitchedOff = (error.response.data as { error_code?: string } | undefined)?.error_code === 'API_NOT_ENABLED';
          if (!isAuthProbe && !isApiSwitchedOff && !window.location.pathname.includes('/admin/unauthorized')) {
            const router = getRouterRef();
            if (router) {
              router.navigate({ to: '/admin/unauthorized', search: { forbidden: '1' } });
            }
          }
          return Promise.reject(error);
        }

        // Handle 429 Rate Limit
        if (error.response?.status === HTTP_STATUS.TOO_MANY_REQUESTS) {
          const retryAfter = parseInt(error.response.headers['retry-after'] ?? '60', 10);
          // Emit rate limit event
          rateLimitEmitter.emit(retryAfter);
        }

        // Retry logic for retryable errors (GET/HEAD only — see isRetryableError)
        if (config && !config.__skipRetry && isRetryableError(error, config)) {
          const retryCount = config.__retryCount ?? 0;

          if (retryCount < API_RETRY_CONFIG.maxRetries) {
            config.__retryCount = retryCount + 1;

            // Get retry delay (use Retry-After header for 429)
            const retryAfter = error.response?.status === HTTP_STATUS.TOO_MANY_REQUESTS
              ? retryAfterSeconds(error)
              : undefined;
            const delay = calculateRetryDelay(retryCount, retryAfter);

            // Wait before retrying
            await new Promise((resolve) => { setTimeout(resolve, delay); });

            // Retry the request
            return this.client.request(config);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Create an AbortController for a request and store it by key.
   * Useful for cancelling requests when components unmount.
   */
  createAbortController(key: string): AbortController {
    // Cancel existing controller if present
    this.cancelRequest(key);

    const controller = new AbortController();
    this.abortControllers.set(key, controller);
    return controller;
  }

  /**
   * Cancel a request by its key.
   */
  cancelRequest(key: string): void {
    const controller = this.abortControllers.get(key);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(key);
    }
  }

  /**
   * Cancel all pending requests.
   */
  cancelAllRequests(): void {
    this.abortControllers.forEach((controller) => controller.abort());
    this.abortControllers.clear();
  }

  // ============================================
  // Authentication (HTTP-only Cookie Based)
  // ============================================

  /**
   * Logout and clear the HTTP-only session cookie.
   */
  async logout(): Promise<void> {
    await this.client.post('/admin/auth/logout');
  }

  /**
   * Check current session status.
   *
   * @returns Session info if authenticated, or { authenticated: false }
   */
  async checkSession(): Promise<{
    authenticated: boolean;
    user?: AdminUser;
    expiresAt?: string;
    /** The server's clock when it answered (ISO). */
    serverTime?: string;
  }> {
    const response = await this.client.get('/admin/auth/session');
    return {
      authenticated: response.data.authenticated,
      user: response.data.user,
      expiresAt: response.data.expiresAt,
      serverTime: response.data.server_time,
    };
  }

  /**
   * Fetch the authenticated user's full profile (server's `/admin/auth/profile`).
   * Used by self-detail surfaces to render fields like MFA / last login that
   * aren't on the lightweight session payload.
   */
  async getProfile(): Promise<UserProfile> {
    const response = await this.client.get('/admin/auth/profile');
    return response.data.profile;
  }

  async updateProfileName(displayName: string): Promise<{ name: string }> {
    const response = await this.client.put('/admin/auth/profile/name', { displayName });
    return { name: response.data.name };
  }

  async triggerProfilePasswordReset(): Promise<PasswordResetIssued> {
    const response = await this.client.post('/admin/auth/profile/password-reset');
    return pickPasswordResetIssued(response.data);
  }

  // ============================================
  // User Management
  // ============================================

  async createUser(input: CreateUserInput): Promise<CreateUserResult> {
    // No automatic retry: the first attempt may already have written the user
    // and membership before failing (a 502 INVITE_EMAIL_FAILED, say), and a
    // replayed POST would then 409 on the membership and hide the real outcome.
    const response = await this.client.post('/admin/users', input, { __skipRetry: true } as Partial<RetryConfig>);
    const { user, created, invite } = response.data as {
      user: User;
      created?: boolean;
      invite?: CreateUserResult['invite'];
    };
    return { ...user, created: created ?? true, ...(invite ? { invite } : {}) };
  }

  async listUsers(filters?: UserFilter): Promise<PaginatedUsersResponse> {
    const response = await this.client.get('/admin/users', { params: filters });
    return {
      users: response.data.users,
      total: response.data.total,
      limit: response.data.limit,
      offset: response.data.offset,
    };
  }

  async getUser(userId: string): Promise<User> {
    const response = await this.client.get(`/admin/users/${encodeURIComponent(userId)}`);
    return response.data.user;
  }

  async updateUser(userId: string, updates: UpdateUserInput): Promise<User> {
    const response = await this.client.patch(`/admin/users/${encodeURIComponent(userId)}`, updates);
    return response.data.user;
  }

  async updateUserRole(userId: string, role: AdminRole): Promise<User> {
    const response = await this.client.put(`/admin/users/${encodeURIComponent(userId)}/role`, { role });
    return response.data.user;
  }

  async updateUserName(userId: string, displayName: string): Promise<{ name: string }> {
    const response = await this.client.put(`/admin/users/${encodeURIComponent(userId)}/name`, { displayName });
    return { name: response.data.name };
  }

  async deleteUser(userId: string): Promise<void> {
    await this.client.delete(`/admin/users/${encodeURIComponent(userId)}`);
  }

  async getUserActivity(userId: string, days: number = 30): Promise<Record<string, unknown>> {
    const response = await this.client.get(`/admin/users/${encodeURIComponent(userId)}/activity`, {
      params: { days },
    });
    return response.data.activity;
  }

  async triggerPasswordReset(userId: string): Promise<PasswordResetIssued> {
    const response = await this.client.post(`/admin/users/${encodeURIComponent(userId)}/password-reset`);
    return pickPasswordResetIssued(response.data);
  }

  // ============================================
  // Customer API keys (the Developer API page)
  // ============================================

  /** The organisation's keys (masked), whether the API is on, and the limits in force. */
  async listApiKeys(): Promise<ApiKeysResponse> {
    const response = await this.client.get('/admin/api-keys');
    return response.data;
  }

  /**
   * Create a key. The full key is in this one answer only. Never retried: a
   * retried POST whose first attempt landed would mint a second key.
   */
  async createApiKey(input: CreateApiKeyInput): Promise<IssuedApiKey> {
    const response = await this.client.post('/admin/api-keys', input, { __skipRetry: true } as Partial<RetryConfig>);
    return { key: response.data.key, secret: response.data.secret };
  }

  /** Replace a key; the old one keeps working for the overlap. Never retried, like create. */
  async rotateApiKey(keyId: string, overlapHours: RotationOverlapHours): Promise<RotatedApiKey> {
    const response = await this.client.post(
      `/admin/api-keys/${encodeURIComponent(keyId)}/rotate`,
      { overlap_hours: overlapHours },
      { __skipRetry: true } as Partial<RetryConfig>,
    );
    return { key: response.data.key, previous_key: response.data.previous_key, secret: response.data.secret };
  }

  async revokeApiKey(keyId: string): Promise<ApiKeyListing> {
    const response = await this.client.post(`/admin/api-keys/${encodeURIComponent(keyId)}/revoke`, {});
    return response.data.key;
  }

  async renameApiKey(keyId: string, name: string): Promise<ApiKeyListing> {
    const response = await this.client.patch(`/admin/api-keys/${encodeURIComponent(keyId)}`, { name });
    return response.data.key;
  }

  // ============================================
  // Analytics
  // ============================================

  async getSystemMetrics(): Promise<SystemMetrics> {
    const response = await this.client.get('/admin/analytics/system');
    return response.data.metrics;
  }

  /** Live cluster fleet roster + shared-infrastructure dependencies. */
  async getClusterStatus(): Promise<ClusterStatus> {
    // The System page re-polls this every ~2.5s, so a dead/restarting backend
    // should fail FAST. Skip the client's network-error retries — otherwise the
    // 1 initial + 3 retries (with exponential backoff) all elapse before the
    // promise rejects, so the UI keeps showing the stale roster for ~4 failed
    // calls before it can flip to "reconnecting". The next poll retries anyway.
    const response = await this.client.get('/admin/cluster', { __skipRetry: true } as Partial<RetryConfig>);
    return response.data.data;
  }

  async getLandingStats(): Promise<{
    hoursSavedThisMonth: number;
    emailsHandledThisMonth: number;
    voiceMinutesThisMonth: number;
    messagesHandledThisMonth: number;
    emailsHandledTotal: number;
    voiceMinutesTotal: number;
    messagesHandledTotal: number;
    avgResponseMs: number | null;
    asOf: string;
  }> {
    const response = await this.client.get('/admin/landing/stats');
    return {
      hoursSavedThisMonth: response.data.hoursSavedThisMonth ?? 0,
      emailsHandledThisMonth: response.data.emailsHandledThisMonth ?? 0,
      voiceMinutesThisMonth: response.data.voiceMinutesThisMonth ?? 0,
      messagesHandledThisMonth: response.data.messagesHandledThisMonth ?? 0,
      emailsHandledTotal: response.data.emailsHandledTotal ?? 0,
      voiceMinutesTotal: response.data.voiceMinutesTotal ?? 0,
      messagesHandledTotal: response.data.messagesHandledTotal ?? 0,
      avgResponseMs: response.data.avgResponseMs ?? null,
      asOf: response.data.asOf ?? new Date(0).toISOString(),
    };
  }

  async getBusinessMetrics(): Promise<BusinessMetrics> {
    const response = await this.client.get('/admin/analytics/business');
    return response.data.metrics;
  }

  async getTimeSeries(
    metric: string,
    startDate: Date,
    endDate: Date,
    granularity: 'minute' | 'hour' | 'day' = 'hour'
  ): Promise<TimeSeries> {
    const response = await this.client.get(`/admin/analytics/timeseries/${metric}`, {
      params: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        granularity,
      },
    });
    return response.data.timeSeries;
  }

  async generateSystemReport(filters: ReportFilter): Promise<Report> {
    const response = await this.client.post('/admin/analytics/reports/system', {
      startDate: filters.startDate.toISOString(),
      endDate: filters.endDate.toISOString(),
      granularity: filters.granularity,
    });
    return response.data.report;
  }

  async generateBusinessReport(filters: ReportFilter): Promise<Report> {
    const response = await this.client.post('/admin/analytics/reports/business', {
      startDate: filters.startDate.toISOString(),
      endDate: filters.endDate.toISOString(),
      granularity: filters.granularity,
    });
    return response.data.report;
  }

  async listReports(filters?: { type?: string; createdBy?: string }): Promise<Report[]> {
    const response = await this.client.get('/admin/analytics/reports', { params: filters });
    return response.data.reports;
  }

  async getReport(reportId: string): Promise<Report> {
    const response = await this.client.get(`/admin/analytics/reports/${reportId}`);
    return response.data.report;
  }

  // ============================================
  // Audit Logs
  // ============================================

  async queryAuditLogs(filter: AuditLogFilter): Promise<{ logs: AuditLogEntry[]; total: number }> {
    const params: Record<string, unknown> = { ...filter };
    if (filter.startDate) params.startDate = filter.startDate.toISOString();
    if (filter.endDate) params.endDate = filter.endDate.toISOString();

    const response = await this.client.get('/admin/audit/logs', { params });
    return {
      logs: response.data.logs,
      total: response.data.total,
    };
  }

  async getAuditLog(logId: string): Promise<AuditLogDetail> {
    const response = await this.client.get(`/admin/audit/logs/${logId}`);
    return response.data.log;
  }

  async getSecurityEvents(limit: number = 100): Promise<AuditLogEntry[]> {
    const response = await this.client.get('/admin/audit/security', { params: { limit } });
    return response.data.logs;
  }

  async getAuditStats(startDate?: Date, endDate?: Date): Promise<AuditLogStats> {
    const params: Record<string, unknown> = {};
    if (startDate) params.startDate = startDate.toISOString();
    if (endDate) params.endDate = endDate.toISOString();

    const response = await this.client.get('/admin/audit/stats', { params });
    return response.data.stats;
  }

  /**
   * Every audit row matching the filter (the page size is not sent: an export
   * is never one page), plus the filename the server chose, when the
   * Content-Disposition header is readable.
   */
  async exportAuditLogs(
    format: 'json' | 'csv',
    filter?: AuditLogFilter,
  ): Promise<{ blob: Blob; filename: string | undefined }> {
    const { limit: _limit, offset: _offset, ...filters } = filter ?? {};
    const params: Record<string, unknown> = { format, ...filters };
    if (filter?.startDate) params.startDate = filter.startDate.toISOString();
    if (filter?.endDate) params.endDate = filter.endDate.toISOString();

    const response = await this.client.get('/admin/audit/export', {
      params,
      responseType: 'blob',
    });
    const disposition = String(response.headers?.['content-disposition'] ?? '');
    const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1];
    return { blob: response.data, filename };
  }

  // ============================================
  // System Control
  // ============================================

  async clearCache(): Promise<void> {
    await this.client.post('/admin/system/cache/clear');
  }

  async getSystemHealth(): Promise<Record<string, unknown>> {
    const response = await this.client.get('/admin/system/health');
    return response.data.health;
  }

  // ============================================
  // Agent Management
  // ============================================

  async listAgents(): Promise<Agent[]> {
    const response = await this.client.get('/admin/agents');
    return response.data.agents;
  }

  async getAgent(name: string): Promise<Agent> {
    const response = await this.client.get(`/admin/agents/${encodeURIComponent(name)}`);
    return response.data.agent;
  }

  async createAgent(input: CreateAgentInput): Promise<Agent> {
    const response = await this.client.post('/admin/agents', input);
    return response.data.agent;
  }

  async updateAgent(name: string, updates: UpdateAgentInput): Promise<Agent> {
    const response = await this.client.patch(`/admin/agents/${encodeURIComponent(name)}`, updates);
    return response.data.agent;
  }

  async deleteAgent(name: string): Promise<void> {
    await this.client.delete(`/admin/agents/${encodeURIComponent(name)}`);
  }

  /** One page of an agent's versions, newest first. */
  async listAgentVersions(
    name: string,
    paging: { page: number; page_size: number }
  ): Promise<AgentVersionPage> {
    const response = await this.client.get(`/admin/agents/${encodeURIComponent(name)}/versions`, {
      params: paging,
    });
    const { versions, total, page, page_size, currentVersion } = response.data;
    return { versions, total, page, page_size, currentVersion };
  }

  /**
   * The settings that differ for a version: against the version before it
   * (what it changed) or the live agent (what has changed since).
   */
  async compareAgentVersions(
    name: string,
    version: number,
    against: AgentVersionCompareTarget
  ): Promise<AgentVersionComparison> {
    const response = await this.client.get(
      `/admin/agents/${encodeURIComponent(name)}/versions/${version}/compare`,
      { params: { with: against } }
    );
    const { from, to, changes } = response.data;
    return { from, to, changes };
  }

  /** Save a version's settings as a new version on top. */
  async restoreAgentVersion(
    name: string,
    version: number,
    baseVersion?: number
  ): Promise<AgentVersionRestoreResult> {
    const response = await this.client.post(
      `/admin/agents/${encodeURIComponent(name)}/versions/${version}/restore`,
      baseVersion ? { baseVersion } : {}
    );
    const { agent, version: live, restoredFromVersion } = response.data;
    return { agent, version: live, restoredFromVersion };
  }

  async reloadAgents(): Promise<{ agentCount: number }> {
    const response = await this.client.post('/admin/agents/reload');
    return { agentCount: response.data.agentCount };
  }

  // ============================================
  // Evaluation & A/B Testing
  // ============================================

  async getEvaluationStats(): Promise<EvaluationStats> {
    const response = await this.client.get('/admin/evaluation/stats');
    return response.data.stats;
  }

  async listABTests(status?: string): Promise<ABTest[]> {
    const response = await this.client.get('/admin/evaluation/ab-tests', { params: { status } });
    return response.data.tests;
  }

  async getABTest(testId: string): Promise<ABTest> {
    const response = await this.client.get(`/admin/evaluation/ab-tests/${testId}`);
    return response.data.test;
  }

  async createABTest(test: Partial<ABTest>): Promise<ABTest> {
    const response = await this.client.post('/admin/evaluation/ab-tests', test);
    return response.data.test;
  }

  async updateABTest(testId: string, updates: Partial<ABTest>): Promise<ABTest> {
    const response = await this.client.patch(`/admin/evaluation/ab-tests/${testId}`, updates);
    return response.data.test;
  }

  async startABTest(testId: string): Promise<ABTest> {
    const response = await this.client.post(`/admin/evaluation/ab-tests/${testId}/start`);
    return response.data.test;
  }

  async stopABTest(testId: string): Promise<ABTest> {
    const response = await this.client.post(`/admin/evaluation/ab-tests/${testId}/stop`);
    return response.data.test;
  }

  async getFeedback(filters?: { startDate?: Date; endDate?: Date; rating?: number }): Promise<EvaluationFeedback[]> {
    const params: Record<string, unknown> = { ...filters };
    if (filters?.startDate) params.startDate = filters.startDate.toISOString();
    if (filters?.endDate) params.endDate = filters.endDate.toISOString();
    const response = await this.client.get('/admin/evaluation/feedback', { params });
    return response.data.feedback;
  }

  async getConversationQuality(conversationId: string): Promise<ConversationQuality> {
    const response = await this.client.get(`/admin/evaluation/quality/${conversationId}`);
    return response.data.quality;
  }

  async getFailedConversations(filters?: { priority?: string; resolved?: boolean; limit?: number }): Promise<FailedConversation[]> {
    const response = await this.client.get('/admin/evaluation/failures', { params: filters });
    return response.data.failures;
  }

  async resolveFailedConversation(conversationId: string, resolution: string): Promise<void> {
    await this.client.post(`/admin/evaluation/failures/${conversationId}/resolve`, { resolution });
  }

  // ============================================
  // Audio Quality & Call Monitoring
  // ============================================

  async getAudioSystemStats(): Promise<AudioSystemStats> {
    const response = await this.client.get('/admin/audio/stats');
    return response.data.stats;
  }

  async getActiveCalls(): Promise<ActiveCall[]> {
    const response = await this.client.get('/admin/audio/active-calls');
    return response.data.calls;
  }

  async getCallAudioQuality(callId: string): Promise<AudioQualityMetrics> {
    const response = await this.client.get(`/admin/audio/calls/${callId}/quality`);
    return response.data.metrics;
  }

  async getCallQualityHistory(callId: string): Promise<Array<{ timestamp: Date; snr: number; quality: number; dbfs: number }>> {
    const response = await this.client.get(`/admin/audio/calls/${callId}/history`);
    return response.data.history;
  }

  async getRecentCallsQuality(limit: number = 50): Promise<AudioQualityMetrics[]> {
    const response = await this.client.get('/admin/audio/recent', { params: { limit } });
    return response.data.calls;
  }

  // ============================================
  // Guardrails & Safety
  // ============================================

  async getGuardrailStats(startDate?: Date, endDate?: Date): Promise<GuardrailStats> {
    const params: Record<string, unknown> = {};
    if (startDate) params.startDate = startDate.toISOString();
    if (endDate) params.endDate = endDate.toISOString();
    const response = await this.client.get('/admin/guardrails/stats', { params });
    return response.data.stats;
  }

  async getGuardrailEvents(filters?: { type?: string; severity?: string; limit?: number }): Promise<GuardrailEvent[]> {
    const response = await this.client.get('/admin/guardrails/events', { params: filters });
    return response.data.events;
  }

  // ============================================
  // Email Templates
  // ============================================

  async listEmailTemplates(filters?: {
    category?: string;
    isActive?: boolean;
    tags?: string;
    search?: string;
  }): Promise<EmailTemplate[]> {
    const response = await this.client.get('/admin/templates', { params: filters });
    return response.data.templates;
  }

  async getEmailTemplate(templateId: string): Promise<EmailTemplate> {
    const response = await this.client.get(`/admin/templates/${encodeURIComponent(templateId)}`);
    return response.data.template;
  }

  async createEmailTemplate(input: CreateEmailTemplateInput): Promise<EmailTemplate> {
    const response = await this.client.post('/admin/templates', input);
    return response.data.template;
  }

  async updateEmailTemplate(templateId: string, updates: UpdateEmailTemplateInput): Promise<EmailTemplate> {
    const response = await this.client.patch(`/admin/templates/${encodeURIComponent(templateId)}`, updates);
    return response.data.template;
  }

  async deleteEmailTemplate(templateId: string, hard: boolean = false): Promise<{ deleted: boolean; message: string }> {
    const response = await this.client.delete(`/admin/templates/${encodeURIComponent(templateId)}`, {
      params: { hard },
    });
    return { deleted: response.data.deleted, message: response.data.message };
  }

  async getEmailTemplateVersions(templateId: string): Promise<EmailTemplateVersion[]> {
    const response = await this.client.get(`/admin/templates/${encodeURIComponent(templateId)}/versions`);
    return response.data.versions;
  }

  async restoreEmailTemplateVersion(templateId: string, version: number): Promise<EmailTemplate> {
    const response = await this.client.post(`/admin/templates/${encodeURIComponent(templateId)}/restore/${version}`);
    return response.data.template;
  }

  async previewEmailTemplate(templateId: string, data: Record<string, unknown>, agentId?: string): Promise<EmailTemplatePreview> {
    const response = await this.client.post(`/admin/templates/${encodeURIComponent(templateId)}/preview`, {
      data,
      agentId,
    });
    return response.data.preview;
  }

  async duplicateEmailTemplate(templateId: string, newTemplateId: string, newName: string): Promise<EmailTemplate> {
    const response = await this.client.post(`/admin/templates/${encodeURIComponent(templateId)}/duplicate`, {
      newTemplateId,
      newName,
    });
    return response.data.template;
  }

  // ============================================
  // Voice Configuration
  // ============================================

  async listVoiceConfigs(filters?: {
    agentId?: string;
    provider?: TTSProviderType;
    active?: boolean;
  }): Promise<VoiceConfig[]> {
    const response = await this.client.get('/admin/voice-configs', { params: filters });
    return response.data.voiceConfigs;
  }

  /**
   * Chat-capable models the given provider serves. Populated from the provider,
   * not a frontend constant — the constant drifted badly enough that the model
   * voice calls actually ran on was not in the list.
   */
  async getProviderModels(provider: 'openai' | 'anthropic'): Promise<CatalogOption[]> {
    const response = await this.client.get('/admin/config/models', { params: { provider } });
    return response.data.models;
  }

  /**
   * Which LLM providers this server holds an API key for. Lets the agent form
   * grey out a fallback provider that cannot work here rather than offer it.
   */
  async getLlmProviders(): Promise<LlmProviderAvailability[]> {
    const response = await this.client.get('/admin/config/llm-providers');
    return response.data.providers;
  }

  /** Voices on the configured ElevenLabs account. */
  async getProviderVoices(): Promise<VoiceOption[]> {
    const response = await this.client.get('/admin/config/voices');
    return response.data.voices;
  }

  /**
   * Hang up a call this tenant owns. These are outcomes the caller shows, not
   * errors: 404 (not this tenant's, or unknown) and 409 (already over) mean
   * there is nothing left to end; 502 means Twilio couldn't be reached and the
   * call may still be live, so it is worth trying again.
   */
  async endCall(
    callSid: string,
  ): Promise<{ ended: true } | { ended: false; retryable: boolean; message: string }> {
    const response = await this.client.post(`/admin/calls/${encodeURIComponent(callSid)}/end`, undefined, {
      validateStatus: (status) => [200, 404, 409, 502].includes(status),
    });
    if (response.status === 200) return { ended: true };
    const retryable = response.status === 502;
    return {
      ended: false,
      retryable,
      message:
        response.data?.message ??
        (retryable ? 'Could not reach Twilio to end this call. Try again.' : 'This call has already ended.'),
    };
  }

  async getVoiceConfig(id: string): Promise<VoiceConfig> {
    const response = await this.client.get(`/admin/voice-configs/${encodeURIComponent(id)}`);
    return response.data.voiceConfig;
  }

  async createVoiceConfig(input: CreateVoiceConfigInput): Promise<VoiceConfig> {
    const response = await this.client.post('/admin/voice-configs', input);
    return response.data.voiceConfig;
  }

  async updateVoiceConfig(id: string, updates: UpdateVoiceConfigInput): Promise<VoiceConfig> {
    const response = await this.client.patch(`/admin/voice-configs/${encodeURIComponent(id)}`, updates);
    return response.data.voiceConfig;
  }

  async deleteVoiceConfig(id: string): Promise<{ deleted: boolean }> {
    const response = await this.client.delete(`/admin/voice-configs/${encodeURIComponent(id)}`);
    return { deleted: response.data.deleted };
  }

  async setVoiceConfigDefault(id: string): Promise<void> {
    await this.client.post(`/admin/voice-configs/${encodeURIComponent(id)}/set-default`);
  }

  async getTTSProviders(): Promise<TTSProvider[]> {
    const response = await this.client.get('/admin/voice-configs/providers');
    return response.data.providers;
  }

  async getAgentVoiceConfig(agentName: string): Promise<VoiceConfig | null> {
    const response = await this.client.get(`/admin/agents/${encodeURIComponent(agentName)}/voice-configs`);
    return response.data.voiceConfig;
  }

  // ============================================
  // Tool Credentials
  // ============================================

  /**
   * Get masked credentials for an agent's tool
   */
  async getToolCredentials(agentName: string, toolName: string): Promise<ToolCredentialsResponse> {
    const response = await this.client.get(
      `/admin/agents/${encodeURIComponent(agentName)}/tools/${encodeURIComponent(toolName)}/credentials`
    );
    return response.data;
  }

  /**
   * Set credentials for an agent's tool
   */
  async setToolCredentials(
    agentName: string,
    toolName: string,
    credentials: SetToolCredentialsRequest
  ): Promise<ToolCredentialsResponse> {
    const response = await this.client.put(
      `/admin/agents/${encodeURIComponent(agentName)}/tools/${encodeURIComponent(toolName)}/credentials`,
      credentials
    );
    return response.data;
  }

  /**
   * Clear all credentials for an agent's tool
   */
  async clearToolCredentials(agentName: string, toolName: string): Promise<ToolCredentialsResponse> {
    const response = await this.client.delete(
      `/admin/agents/${encodeURIComponent(agentName)}/tools/${encodeURIComponent(toolName)}/credentials`
    );
    return response.data;
  }

  /**
   * Test connection with provided or stored credentials
   */
  async testToolConnection(
    agentName: string,
    toolName: string,
    request?: TestToolConnectionRequest
  ): Promise<TestToolConnectionResponse> {
    const response = await this.client.post(
      `/admin/agents/${encodeURIComponent(agentName)}/tools/${encodeURIComponent(toolName)}/credentials/test`,
      request ?? {}
    );
    return response.data;
  }

  // ============================================
  // Tenant Phone Numbers
  // ============================================

  /** The signed-in customer's own assigned numbers (config:read). */
  async listOwnPhoneNumbers(): Promise<TenantPhoneNumber[]> {
    const response = await this.client.get('/admin/phone-numbers');
    return response.data.phone_numbers;
  }

  /** Name or describe one of the customer's numbers (config:update). */
  async updatePhoneNumberDetails(
    phoneNumberId: string,
    input: UpdatePhoneNumberDetailsInput
  ): Promise<TenantPhoneNumber> {
    const response = await this.client.patch(
      `/admin/phone-numbers/${encodeURIComponent(phoneNumberId)}`,
      input
    );
    return response.data.phone_number;
  }

  /** One page of a number's history, newest first (audit_log:read). */
  async getPhoneNumberHistory(
    phoneNumberId: string,
    paging: { page: number; page_size: number }
  ): Promise<PhoneNumberHistoryPage> {
    const response = await this.client.get(
      `/admin/phone-numbers/${encodeURIComponent(phoneNumberId)}/history`,
      { params: paging }
    );
    const { entries, total, page, page_size } = response.data;
    return { entries, total, page, page_size };
  }

  /** The kinds of number the tenant can buy, their price in credits, and its balance (admins). */
  async getNumberPurchaseOptions(): Promise<NumberPurchaseOptions> {
    const response = await this.client.get('/admin/phone-numbers/purchase-options');
    const { kinds, balance_credits, max_results } = response.data;
    return { kinds, balance_credits, max_results };
  }

  /** Up to 10 numbers of a kind for sale now; an area code for landlines only (admins). */
  async searchAvailableNumbers(
    query: { kind: string; area_code?: string }
  ): Promise<{ numbers: AvailablePhoneNumber[]; monthly_credits: number }> {
    const response = await this.client.get('/admin/phone-numbers/available', { params: query });
    return { numbers: response.data.numbers, monthly_credits: response.data.monthly_credits };
  }

  /** The current number terms, accepted before buying a number or adding a caller ID (admins). */
  async getNumberTerms(): Promise<NumberTerms> {
    const response = await this.client.get('/admin/phone-numbers/terms');
    return { version: response.data.version, text: response.data.text };
  }

  /**
   * Buy a number the search offered, accepting the terms version shown
   * (admins). A purchase Twilio has not confirmed yet answers 202: no number
   * yet, and `pending` says what happens next.
   */
  async purchasePhoneNumber(input: {
    phone_number: string;
    kind: string;
    name?: string | null;
    termsVersion: string;
  }): Promise<
    | { phone_number: TenantPhoneNumber; charged_credits: number | null; pending?: undefined }
    | { phone_number: null; charged_credits: null; pending: string }
  > {
    const response = await this.client.post('/admin/phone-numbers/purchase', input);
    if (response.status === 202) {
      return { phone_number: null, charged_credits: null, pending: String(response.data.error ?? 'The number is still being set up.') };
    }
    return { phone_number: response.data.phone_number, charged_credits: response.data.charged_credits };
  }

  /** Start verifying an office line as a caller ID; the answer's code is shown once (admins). */
  async startCallerIdVerification(input: {
    number: string;
    name?: string | null;
    attestation: true;
    termsVersion: string;
  }): Promise<CallerIdVerificationStarted> {
    const response = await this.client.post('/admin/phone-numbers/caller-ids', input);
    const { phone_number, validation_code, expires_at } = response.data;
    return { phone_number, validation_code, expires_at };
  }

  /** Remove a caller ID, or cancel one waiting for its verification call (admins). */
  async removeCallerId(phoneNumberId: string): Promise<void> {
    await this.client.delete(`/admin/phone-numbers/caller-ids/${encodeURIComponent(phoneNumberId)}`);
  }

  /** Give a number back, typing it out to confirm (admins). */
  async giveBackPhoneNumber(phoneNumberId: string, confirmPhoneNumber: string): Promise<GivenBackPhoneNumber> {
    const response = await this.client.post(
      `/admin/phone-numbers/${encodeURIComponent(phoneNumberId)}/give-back`,
      { confirm_phone_number: confirmPhoneNumber }
    );
    return { released: response.data.released, phone_number: response.data.phone_number };
  }

  /** Keep a number after all, before the date it is given back on (admins). */
  async undoGiveBackPhoneNumber(phoneNumberId: string): Promise<GivenBackPhoneNumber> {
    const response = await this.client.post(
      `/admin/phone-numbers/${encodeURIComponent(phoneNumberId)}/give-back/undo`
    );
    return { released: false, phone_number: response.data.phone_number };
  }

  // ============================================
  // Integration Profiles
  // ============================================

  /**
   * List all integration profiles for a type (e.g., 'swiftcase')
   */
  async listIntegrationProfiles(
    integrationType: string,
    includeInactive: boolean = false
  ): Promise<IntegrationProfileSummary[]> {
    const response = await this.client.get(
      `/admin/integrations/${encodeURIComponent(integrationType)}`,
      { params: { includeInactive } }
    );
    return response.data.profiles;
  }

  /**
   * Get a specific integration profile by ID
   */
  async getIntegrationProfile(
    integrationType: string,
    profileId: string
  ): Promise<IntegrationProfileDetail> {
    const response = await this.client.get(
      `/admin/integrations/${encodeURIComponent(integrationType)}/${encodeURIComponent(profileId)}`
    );
    return response.data.profile;
  }

  /**
   * Create a new integration profile
   */
  async createIntegrationProfile(
    integrationType: string,
    input: CreateIntegrationProfileInput
  ): Promise<IntegrationProfileDetail> {
    const response = await this.client.post(
      `/admin/integrations/${encodeURIComponent(integrationType)}`,
      input
    );
    return response.data.profile;
  }

  /**
   * Update an integration profile
   */
  async updateIntegrationProfile(
    integrationType: string,
    profileId: string,
    updates: UpdateIntegrationProfileInput
  ): Promise<IntegrationProfileDetail> {
    const response = await this.client.put(
      `/admin/integrations/${encodeURIComponent(integrationType)}/${encodeURIComponent(profileId)}`,
      updates
    );
    return response.data.profile;
  }

  /**
   * Delete an integration profile
   */
  async deleteIntegrationProfile(
    integrationType: string,
    profileId: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete(
      `/admin/integrations/${encodeURIComponent(integrationType)}/${encodeURIComponent(profileId)}`
    );
    return { success: response.data.success, message: response.data.message };
  }

  /**
   * Set a profile as the default for its integration type
   */
  async setIntegrationProfileDefault(
    integrationType: string,
    profileId: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post(
      `/admin/integrations/${encodeURIComponent(integrationType)}/${encodeURIComponent(profileId)}/set-default`
    );
    return { success: response.data.success, message: response.data.message };
  }

  /**
   * Test connection for an integration type with provided credentials or profile
   */
  async testIntegrationConnection(
    integrationType: string,
    request?: TestIntegrationConnectionRequest
  ): Promise<TestIntegrationConnectionResponse> {
    const response = await this.client.post(
      `/admin/integrations/${encodeURIComponent(integrationType)}/test`,
      request ?? {}
    );
    return response.data;
  }

  // ============================================
  // Conversation Methods
  // ============================================

  async getConversations(
    filters?: ConversationFilter,
    page: number = 1,
    limit: number = 20
  ): Promise<PaginatedConversations> {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    
    if (filters) {
      if (filters.agent_name) params.set('agent_name', filters.agent_name);
      if (filters.channel) params.set('channel', filters.channel);
      if (filters.status) params.set('status', filters.status);
      if (filters.caller_phone) params.set('caller_phone', filters.caller_phone);
      if (filters.outcome) params.set('outcome', filters.outcome);
      if (filters.started_after) params.set('started_after', filters.started_after);
      if (filters.started_before) params.set('started_before', filters.started_before);
    }
    
    const response = await this.client.get(`/admin/conversations?${params.toString()}`);
    return response.data;
  }

  async getConversation(id: string): Promise<{ conversation: Conversation; messages: ConversationMessage[] }> {
    const response = await this.client.get(`/admin/conversations/${encodeURIComponent(id)}`);
    return response.data;
  }

  /** A call's recorded events and stored end; platform admins only (anyone else gets a 404). */
  async getConversationDiagnostics(id: string): Promise<ConversationDiagnostics> {
    const response = await this.client.get(`/admin/conversations/${encodeURIComponent(id)}/diagnostics`);
    return response.data.diagnostics;
  }

  async getConversationRecording(id: string, format: 'mp3' | 'wav' = 'mp3'): Promise<{ recording: ConversationRecording }> {
    const response = await this.client.get(`/admin/conversations/${encodeURIComponent(id)}/recording?format=${format}`);
    return response.data;
  }

  getConversationRecordingDownloadUrl(id: string, format: 'mp3' | 'wav' = 'mp3'): string {
    const baseUrl = this.client.defaults.baseURL || '/api';
    return `${baseUrl}/admin/conversations/${encodeURIComponent(id)}/recording/download?format=${format}`;
  }

  async getConversationStats(filters?: {
    agent_id?: string;
    channel?: string;
    started_after?: string;
    started_before?: string;
  }): Promise<ConversationStats> {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.agent_id) params.set('agent_id', filters.agent_id);
      if (filters.channel) params.set('channel', filters.channel);
      if (filters.started_after) params.set('started_after', filters.started_after);
      if (filters.started_before) params.set('started_before', filters.started_before);
    }
    const response = await this.client.get(`/admin/conversations-stats?${params.toString()}`);
    return response.data.stats;
  }

  /** The tenant's calls over a period of UK days, for the Dashboard. */
  async getConversationsOverview(query: ConversationsOverviewQuery): Promise<ConversationsOverview> {
    const response = await this.client.get('/admin/conversations-overview', {
      params: { from: query.from, to: query.to, ...(query.agentId ? { agent_id: query.agentId } : {}) },
    });
    return response.data.overview;
  }
}

// Export singleton instance
export const adminApiClient = new AdminApiClient();
